import { memo, useCallback } from 'react';
import {
  Control,
  Controller,
  UseFormSetValue,
  useWatch,
} from 'react-hook-form';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import { documentTypeOptions } from '../../../shared/google-document-types';
import UsersAutocomplete from '../../../shared/users-autocomplete';
import { domainSharingRadioOptions } from './sharing-options';
import { sizeTypeOptions } from '../../../shared/file-size-utils';
import type { RulesEngineFormValues } from './rules-engine-form-page';
import {
  BOOLEAN_SHARE_FIELD_NAMES,
  DRIVE_FIELD_INFO_TYPES,
} from './condition-block-constants';

import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Grid from '@mui/material/Grid';
import IconButton from '@mui/material/IconButton';
import MenuItem from '@mui/material/MenuItem';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import Tooltip from '@mui/material/Tooltip';
import Autocomplete from '@mui/material/Autocomplete';
import Avatar from '@mui/material/Avatar';
import Typography from '@mui/material/Typography';

interface DriveConditionBlockProps {
  blockIndex: number;
  control: Control<RulesEngineFormValues>;
  setValue: UseFormSetValue<RulesEngineFormValues>;
  conditions: any[];
  conditionBlocks: any[];
  domains: string[];
  getFieldOptions: (
    infoType: string
  ) => Array<{ label: string; value: string }>;
  customOptions: any[];
  fieldInfoTypes: { label: string; value: string }[];
  triggerGroup?: string;
  gPanelRoles: string[];
}

interface DriveConditionRowProps {
  blockIndex: number;
  conditionIndex: number;
  control: Control<RulesEngineFormValues>;
  setValue: UseFormSetValue<RulesEngineFormValues>;
  conditions: any[];
  domains: string[];
  onRemove: (index: number) => void;
  onLogicalOperatorChange: (index: number, operator: 'AND' | 'OR') => void;
  gPanelRoles: string[];
  triggerGroup?: string;
}

const DriveConditionRow = memo(function DriveConditionRow({
  blockIndex,
  conditionIndex,
  control,
  setValue,
  conditions,
  onRemove,
  onLogicalOperatorChange,
  gPanelRoles,
  triggerGroup,
}: DriveConditionRowProps) {
  const infoType =
    useWatch({
      control,
      name: `conditionBlocks.${blockIndex}.conditions.${conditionIndex}.fieldInfoType`,
    }) || 'fileType';

  const fieldName =
    useWatch({
      control,
      name: `conditionBlocks.${blockIndex}.conditions.${conditionIndex}.fieldName`,
    }) || '';

  const operatorValue =
    useWatch({
      control,
      name: `conditionBlocks.${blockIndex}.conditions.${conditionIndex}.operator`,
    }) || 'equals';

  const isFileType = infoType === 'fileType';
  const isFileSize = infoType === 'fileSize';
  const isFileSharing = infoType === 'fileSharing';
  const isEmployeeInformation = infoType === 'employeeInformation';

  const getFieldOptions = useCallback((type: string) => {
    switch (type) {
      case 'fileType':
        return [{ label: 'File Type', value: 'fileType' }];
      case 'fileSize':
        return [{ label: 'File Size', value: 'fileSize' }];
      case 'fileSharing':
        return [
          {
            label: 'user external to the domain',
            value: 'fileSharedToUserExternalToDomain',
          },
          {
            label: 'user internal to the domain',
            value: 'fileSharedToUserInternalToDomain',
          },
          {
            label: 'anyone with the link (public)',
            value: 'fileIsSharedPublic',
          },
          {
            label: 'specific user',
            value: 'fileSharedToSpecificUser',
          },
          {
            label: 'domain',
            value: 'fileIsSharedToDomain',
          },
        ];
      case 'employeeInformation':
        return [{ label: 'gPanel Role', value: 'gPanelRole' }];
      default:
        return [];
    }
  }, []);

  return (
    <Box>
      {conditionIndex > 0 && (
        <Box
          sx={{
            display: 'flex',
            gap: 1,
            mb: 3,
            alignItems: 'center',
          }}
        >
          <Box sx={{ display: 'flex', gap: 0.5 }}>
            <Button
              variant={
                conditions[conditionIndex - 1]?.logicalOperator !== 'OR'
                  ? 'contained'
                  : 'outlined'
              }
              size="small"
              sx={{ minWidth: '50px', textTransform: 'none' }}
              onClick={() => onLogicalOperatorChange(conditionIndex, 'AND')}
            >
              And
            </Button>
            <Button
              variant={
                conditions[conditionIndex - 1]?.logicalOperator === 'OR'
                  ? 'contained'
                  : 'outlined'
              }
              size="small"
              sx={{ minWidth: '50px', textTransform: 'none' }}
              onClick={() => onLogicalOperatorChange(conditionIndex, 'OR')}
            >
              Or
            </Button>
          </Box>
        </Box>
      )}

      <Grid
        container
        spacing={1}
        alignItems="center"
        sx={{
          pl: 2,
          borderLeft: '2px solid',
          borderLeftColor: 'primary.main',
        }}
      >
        <Grid
          size={{
            xs: 12,
            sm: 6,
            md: triggerGroup === 'drive' && isEmployeeInformation ? 3 : 4,
          }}
        >
          <Controller
            name={`conditionBlocks.${blockIndex}.conditions.${conditionIndex}.fieldInfoType`}
            control={control}
            render={({ field }) => (
              <TextField
                {...field}
                select
                fullWidth
                size="small"
                label="What kind of information?"
                value={field.value || ''}
                onChange={(e) => {
                  const val = e.target.value;
                  field.onChange(val);

                  let defaultFieldName = '';
                  if (val === 'fileSharing') {
                    defaultFieldName = 'fileSharedToUserExternalToDomain';
                  } else if (val === 'employeeInformation') {
                    defaultFieldName = 'gPanelRole';
                  } else {
                    defaultFieldName = val;
                  }

                  setValue(
                    `conditionBlocks.${blockIndex}.conditions.${conditionIndex}.fieldName`,
                    defaultFieldName
                  );
                  // Boolean sharing fields (e.g. "user external to the domain") can never match
                  // the empty string, so they need a real default value here.
                  setValue(
                    `conditionBlocks.${blockIndex}.conditions.${conditionIndex}.value`,
                    BOOLEAN_SHARE_FIELD_NAMES.includes(defaultFieldName)
                      ? 'true'
                      : ''
                  );
                  const defaultOperator =
                    val === 'fileSize' ? 'larger' : 'equals';
                  setValue(
                    `conditionBlocks.${blockIndex}.conditions.${conditionIndex}.operator`,
                    defaultOperator
                  );

                  if (val === 'fileSize') {
                    setValue(
                      `conditionBlocks.${blockIndex}.conditions.${conditionIndex}.secondaryValue`,
                      ''
                    );
                    setValue(
                      `conditionBlocks.${blockIndex}.conditions.${conditionIndex}.unit`,
                      'MB'
                    );
                  }
                }}
              >
                {DRIVE_FIELD_INFO_TYPES.map((type) => (
                  <MenuItem key={type.value} value={type.value}>
                    {type.label}
                  </MenuItem>
                ))}
              </TextField>
            )}
          />
        </Grid>

        {triggerGroup === 'drive' && isEmployeeInformation && (
          <Grid size={{ xs: 12, sm: 6, md: 2 }}>
            <Controller
              name={`conditionBlocks.${blockIndex}.conditions.${conditionIndex}.fieldName`}
              control={control}
              render={({ field, fieldState }) => {
                const currentInfoType =
                  conditions[conditionIndex]?.fieldInfoType;
                const fieldOptions = getFieldOptions(currentInfoType);
                return (
                  <TextField
                    {...field}
                    select
                    fullWidth
                    size="small"
                    label="Which field?"
                    value={field.value || ''}
                    disabled={!currentInfoType}
                    error={!!fieldState.error}
                    helperText={fieldState.error?.message}
                    onChange={(e) => {
                      const val = e.target.value;
                      field.onChange(val);
                      setValue(
                        `conditionBlocks.${blockIndex}.conditions.${conditionIndex}.value`,
                        ''
                      );
                    }}
                  >
                    {fieldOptions.map((option) => (
                      <MenuItem key={option.value} value={option.value}>
                        {option.label}
                      </MenuItem>
                    ))}
                  </TextField>
                );
              }}
            />
          </Grid>
        )}

        <Grid
          size={{
            xs: 12,
            sm: 6,
            md: triggerGroup === 'drive' && isEmployeeInformation ? 3 : 3,
          }}
        >
          <Controller
            name={`conditionBlocks.${blockIndex}.conditions.${conditionIndex}.operator`}
            control={control}
            render={({ field }) => {
              const operatorOptions =
                infoType === 'fileSize'
                  ? [
                      { label: 'is between', value: 'between' },
                      { label: 'less than', value: 'smaller' },
                      { label: 'greater than', value: 'larger' },
                      { label: 'equals', value: 'equals' },
                    ]
                  : [
                      { label: 'equals', value: 'equals' },
                      { label: 'does not equal', value: 'notEquals' },
                    ];

              const defaultOperator =
                infoType === 'fileSize' ? 'larger' : 'equals';

              const validOperators =
                infoType === 'fileSize'
                  ? ['between', 'smaller', 'larger', 'equals']
                  : infoType === 'employeeInformation'
                  ? ['equals', 'notEquals']
                  : ['equals', 'notEquals'];

              const currentOperator = validOperators.includes(field.value)
                ? field.value
                : defaultOperator;

              return (
                <TextField
                  {...field}
                  select
                  fullWidth
                  size="small"
                  label="Operator"
                  value={currentOperator}
                  onChange={(e) => {
                    field.onChange(e.target.value || defaultOperator);
                  }}
                >
                  {operatorOptions.map((op) => (
                    <MenuItem key={op.value} value={op.value}>
                      {op.label}
                    </MenuItem>
                  ))}
                </TextField>
              );
            }}
          />
        </Grid>

        <Grid
          size={{
            xs: 12,
            sm: 6,
            md: triggerGroup === 'drive' && isEmployeeInformation ? 3 : 4,
          }}
        >
          {isFileType && (
            <Controller
              name={`conditionBlocks.${blockIndex}.conditions.${conditionIndex}.value`}
              control={control}
              render={({ field, fieldState }) => {
                const selectedValue = documentTypeOptions.find(
                  (option) => option.value === (field.value || '')
                );
                return (
                  <Autocomplete
                    fullWidth
                    size="small"
                    options={documentTypeOptions}
                    value={selectedValue ?? null}
                    getOptionLabel={(option) => option.displayValue}
                    isOptionEqualToValue={(option, value) =>
                      option.value === value.value
                    }
                    onChange={(_, newValue) => {
                      field.onChange(newValue?.value ?? '');
                    }}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        fullWidth
                        label="File Types"
                        placeholder="Select file type"
                        error={!!fieldState.error}
                        helperText={fieldState.error?.message}
                      />
                    )}
                    renderOption={(props, option) => (
                      <Box component="li" {...props} key={option.value}>
                        <Avatar
                          src={option.icon}
                          sx={{ width: 18, height: 18, mr: 1 }}
                          variant="rounded"
                        />
                        {option.displayValue}
                      </Box>
                    )}
                  />
                );
              }}
            />
          )}

          {isFileSize && (
            <Stack
              direction="row"
              spacing={1}
              alignItems="center"
              flexWrap="wrap"
              sx={{ width: '100%' }}
            >
              <Controller
                name={`conditionBlocks.${blockIndex}.conditions.${conditionIndex}.value`}
                control={control}
                render={({ field, fieldState }) => (
                  <TextField
                    {...field}
                    fullWidth
                    type="number"
                    size="small"
                    label="Size"
                    value={field.value ?? ''}
                    onKeyDown={(e) => {
                      if (e.key === '-') {
                        e.preventDefault();
                      }
                    }}
                    inputProps={{ min: 0 }}
                    onChange={(e) => {
                      const val = e.target.value;
                      if (val && parseFloat(val) < 0) {
                        field.onChange('');
                      } else {
                        field.onChange(val);
                      }
                    }}
                    sx={{ flex: 1, minWidth: 90 }}
                    error={!!fieldState.error}
                    helperText={fieldState.error?.message}
                  />
                )}
              />
              {operatorValue === 'between' && (
                <>
                  <Typography sx={{ px: 0.5 }}>and</Typography>
                  <Controller
                    name={`conditionBlocks.${blockIndex}.conditions.${conditionIndex}.secondaryValue`}
                    control={control}
                    render={({ field, fieldState }) => (
                      <TextField
                        {...field}
                        fullWidth
                        type="number"
                        size="small"
                        label="Size"
                        value={field.value ?? ''}
                        onKeyDown={(e) => {
                          if (e.key === '-') {
                            e.preventDefault();
                          }
                        }}
                        inputProps={{ min: 0 }}
                        onChange={(e) => {
                          const val = e.target.value;
                          if (val && parseFloat(val) < 0) {
                            field.onChange('');
                          } else {
                            field.onChange(val);
                          }
                        }}
                        sx={{ flex: 1, minWidth: 90 }}
                        error={!!fieldState.error}
                        helperText={fieldState.error?.message}
                      />
                    )}
                  />
                </>
              )}
              <Controller
                name={`conditionBlocks.${blockIndex}.conditions.${conditionIndex}.unit`}
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    select
                    fullWidth
                    size="small"
                    label="File Size"
                    value={field.value || 'MB'}
                    onChange={(e) => field.onChange(e.target.value)}
                    sx={{ flex: 1, minWidth: 100 }}
                  >
                    {sizeTypeOptions.map((option) => (
                      <MenuItem key={option.value} value={option.value}>
                        {option.displayValue}
                      </MenuItem>
                    ))}
                  </TextField>
                )}
              />
            </Stack>
          )}

          {isFileSharing && (
            <Stack
              direction="row"
              spacing={1}
              alignItems="center"
              sx={{ width: '100%' }}
            >
              <Controller
                name={`conditionBlocks.${blockIndex}.conditions.${conditionIndex}.fieldName`}
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    select
                    fullWidth
                    size="small"
                    label="Shared to"
                    value={field.value || 'fileSharedToUserExternalToDomain'}
                    onChange={(e) => {
                      const newVal = e.target.value;
                      field.onChange(newVal);
                      const isBooleanShareType =
                        BOOLEAN_SHARE_FIELD_NAMES.includes(newVal);
                      if (isBooleanShareType) {
                        setValue(
                          `conditionBlocks.${blockIndex}.conditions.${conditionIndex}.value`,
                          'true'
                        );
                        if (
                          operatorValue !== 'equals' &&
                          operatorValue !== 'notEquals'
                        ) {
                          setValue(
                            `conditionBlocks.${blockIndex}.conditions.${conditionIndex}.operator`,
                            'equals'
                          );
                        }
                      } else {
                        setValue(
                          `conditionBlocks.${blockIndex}.conditions.${conditionIndex}.value`,
                          ''
                        );
                        setValue(
                          `conditionBlocks.${blockIndex}.conditions.${conditionIndex}.operator`,
                          'equals'
                        );
                      }
                    }}
                    sx={{ flex: 1, minWidth: 220 }}
                  >
                    {getFieldOptions('fileSharing').map((option) => (
                      <MenuItem key={option.value} value={option.value}>
                        {option.label}
                      </MenuItem>
                    ))}
                  </TextField>
                )}
              />
              {fieldName === 'fileSharedToSpecificUser' && (
                <Box sx={{ flex: 1, minWidth: 220 }}>
                  <Controller
                    name={`conditionBlocks.${blockIndex}.conditions.${conditionIndex}.value`}
                    control={control}
                    render={({ field, fieldState }) => (
                      <UsersAutocomplete
                        autocompleteValue={field.value || ''}
                        setAutoCompleteValue={(value) => field.onChange(value)}
                        label="Search for a user"
                        size="small"
                        errors={!!fieldState.error}
                        helperText={fieldState.error?.message}
                      />
                    )}
                  />
                </Box>
              )}
              {fieldName === 'fileIsSharedToDomain' && (
                <Box sx={{ flex: 1, minWidth: 220 }}>
                  <Controller
                    name={`conditionBlocks.${blockIndex}.conditions.${conditionIndex}.value`}
                    control={control}
                    render={({ field, fieldState }) => (
                      <TextField
                        {...field}
                        select
                        fullWidth
                        size="small"
                        label="Specific Domain Filter(Optional)"
                        value={field.value || ''}
                        error={!!fieldState.error}
                        helperText={fieldState.error?.message}
                        onChange={(e) => field.onChange(e.target.value)}
                      >
                        <MenuItem value="">
                          <em>None</em>
                        </MenuItem>
                        {domainSharingRadioOptions.map((option) => (
                          <MenuItem key={option.value} value={option.value}>
                            {option.displayValue}
                          </MenuItem>
                        ))}
                      </TextField>
                    )}
                  />
                </Box>
              )}
            </Stack>
          )}

          {isEmployeeInformation && (
            <Controller
              name={`conditionBlocks.${blockIndex}.conditions.${conditionIndex}.value`}
              control={control}
              render={({ field, fieldState }) => (
                <TextField
                  {...field}
                  select
                  fullWidth
                  size="small"
                  label="gPanel Role"
                  error={!!fieldState.error}
                  helperText={fieldState.error?.message}
                  value={field.value || ''}
                  onChange={(e) => field.onChange(e.target.value)}
                >
                  {(gPanelRoles || []).map((role) => (
                    <MenuItem key={role} value={role}>
                      {role}
                    </MenuItem>
                  ))}
                </TextField>
              )}
            />
          )}
        </Grid>

        <Grid
          size={{ xs: 12, sm: 12, md: 1 }}
          sx={{ display: 'flex', alignItems: 'center', minHeight: 40 }}
        >
          <Tooltip title="Delete condition">
            <IconButton size="small" onClick={() => onRemove(conditionIndex)}>
              <DeleteOutlineIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Grid>
      </Grid>
    </Box>
  );
});

const DriveConditionBlock = memo(function DriveConditionBlock({
  blockIndex,
  control,
  setValue,
  conditions,
  conditionBlocks,
  domains,
  triggerGroup,
  gPanelRoles,
}: DriveConditionBlockProps) {
  const handleLogicalOperatorChange = useCallback(
    (conditionIndex: number, operator: 'AND' | 'OR') => {
      const updatedConditions = [...conditions];
      updatedConditions[conditionIndex - 1] = {
        ...updatedConditions[conditionIndex - 1],
        logicalOperator: operator,
      };
      const updatedBlock = {
        ...conditionBlocks[blockIndex],
        conditions: updatedConditions,
      };
      const updatedBlocks = [...conditionBlocks];
      updatedBlocks[blockIndex] = updatedBlock;
      setValue('conditionBlocks', updatedBlocks);
    },
    [conditions, conditionBlocks, blockIndex, setValue]
  );

  const ensureValidOperator = useCallback((condition: any): any => {
    if (!condition.operator) {
      const defaultOperator =
        condition.fieldInfoType === 'fileSize' ? 'larger' : 'equals';
      return { ...condition, operator: defaultOperator };
    }
    return condition;
  }, []);

  const handleRemove = useCallback(
    (conditionIndex: number) => {
      const updatedConditions = conditions
        .filter((_: any, i: number) => i !== conditionIndex)
        .map((cond: any) => ensureValidOperator(cond));
      if (updatedConditions.length === 0) {
        setValue(
          'conditionBlocks',
          conditionBlocks.filter(
            (_: any, index: number) => index !== blockIndex
          )
        );
        return;
      }
      const updatedBlock = {
        ...conditionBlocks[blockIndex],
        conditions: updatedConditions,
      };
      const updatedBlocks = [...conditionBlocks];
      updatedBlocks[blockIndex] = updatedBlock;
      setValue('conditionBlocks', updatedBlocks);
    },
    [conditions, conditionBlocks, blockIndex, setValue, ensureValidOperator]
  );

  return (
    <Stack gap={2}>
      {conditions.map((condition, conditionIndex) => {
        return (
          <DriveConditionRow
            key={condition.id}
            blockIndex={blockIndex}
            conditionIndex={conditionIndex}
            control={control}
            setValue={setValue}
            conditions={conditions}
            domains={domains}
            onRemove={handleRemove}
            onLogicalOperatorChange={handleLogicalOperatorChange}
            gPanelRoles={gPanelRoles}
            triggerGroup={triggerGroup}
          />
        );
      })}
    </Stack>
  );
});

export default DriveConditionBlock;
